import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { useToast } from '../components/ToastProvider';
import type { FlowId, StepId } from '../types';
import type { WizardContextValue } from './WizardContext';
import { resolveFlow } from './flowRegistry';
import { FlowConfig } from '../config/flowConfig';

const PAS_FLOW_STEP_ALIASES: Record<string, string> = {
  child_passport_info: 'child_info',
  declaration_lost_passport: 'declaration_lost'
};
import { useWizardStore } from './wizardStore';
import { useFlowSync } from './hooks/useFlowSync';
import { useWizardSessionBootstrap } from './hooks/useWizardSessionBootstrap';
import { useWizardRouteSync } from './hooks/useWizardRouteSync';
import { useWizardPersistence } from './hooks/useWizardPersistence';
import { useStepFocus } from './hooks/useStepFocus';
import { useWizardNavigation } from './hooks/useWizardNavigation';
import { useWizardProgress } from './hooks/useWizardProgress';
import { resolveFlowIdFromSources } from './utils/routing';
import type { WizardProgress } from './utils/progress';

interface WizardRuntimeResult {
  isLoading: boolean;
  currentStepId: StepId;
  progress: WizardProgress;
  stepName?: string;
  contextValue: WizardContextValue;
}

export const useWizardRuntime = (): WizardRuntimeResult => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const {
    flowId,
    setFlowId,
    selfServiceData,
    replaceSelfServiceData,
    updateSelfServiceData,
    currentStepId,
    setCurrentStepId,
    history,
    pushHistory,
    popHistory,
    resetHistory,
    sessionId,
    setSessionId,
    setNavHandlers
  } = useWizardStore(state => ({
    flowId: state.flowId,
    setFlowId: state.setFlowId,
    selfServiceData: state.selfServiceData,
    replaceSelfServiceData: state.replaceSelfServiceData,
    updateSelfServiceData: state.updateSelfServiceData,
    currentStepId: state.currentStepId,
    setCurrentStepId: state.setCurrentStepId,
    history: state.history,
    pushHistory: state.pushHistory,
    popHistory: state.popHistory,
    resetHistory: state.resetHistory,
    sessionId: state.sessionId,
    setSessionId: state.setSessionId,
    setNavHandlers: state.setNavHandlers
  }), shallow);

  const routeStepId = params.stepId as StepId | undefined;
  const routeFlowParam = params.flowId as string | undefined;
  const queryFlowParam = useMemo(() => new URLSearchParams(location.search).get('flow'), [location.search]);
  const pathFlowParam = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    return segments[0] ?? null;
  }, [location.pathname]);

  const resolvedFlowId: FlowId = resolveFlowIdFromSources({
    routeFlow: routeFlowParam ?? null,
    queryFlow: queryFlowParam,
    pathFlow: pathFlowParam,
    fallback: 'pas'
  });

  useFlowSync({ flowId, resolvedFlowId, setFlowId });

  const activeFlow = useMemo(() => resolveFlow(resolvedFlowId), [resolvedFlowId]);
  const activeSteps = activeFlow.steps;
  const startStep = activeFlow.startStep;
  const displayStepId = useMemo(() => {
    if (resolvedFlowId !== 'pas') {
      return currentStepId;
    }
    return PAS_FLOW_STEP_ALIASES[currentStepId] ?? currentStepId;
  }, [resolvedFlowId, currentStepId]);

  const lastStepRef = useRef<StepId>(startStep);
  const navSourceRef = useRef<null | 'programmatic'>(null);

  useEffect(() => {
    lastStepRef.current = startStep;
  }, [startStep]);

  const isLoading = useWizardSessionBootstrap({
    startStep,
    resolvedFlowId,
    routeStepId,
    activeSteps,
    setCurrentStepId,
    replaceSelfServiceData,
    resetHistory,
    pushHistory,
    setSessionId,
    navigate,
    locationSearch: location.search,
    navSourceRef,
    lastStepRef,
    showToast
  });

  useWizardRouteSync({
    routeStepId,
    activeSteps,
    startStep,
    currentStepId,
    setCurrentStepId,
    history,
    pushHistory,
    resetHistory,
    navSourceRef,
    lastStepRef
  });

  useWizardPersistence({
    sessionId,
    isLoading,
    selfServiceData,
    currentStepId,
    history
  });

  useStepFocus(currentStepId);

  const { goToStep, goBack, goNext } = useWizardNavigation({
    currentStepId,
    startStep,
    resolvedFlowId,
    navigate,
    locationSearch: location.search,
    pushHistory,
    popHistory,
    setCurrentStepId,
    showToast,
    activeSteps,
    selfServiceData,
    navSourceRef,
    lastStepRef
  });

  useEffect(() => {
    setNavHandlers({ goToStep, goBack, goNext });
  }, [goToStep, goBack, goNext, setNavHandlers]);

  const progress = useWizardProgress({
    history,
    currentStepId,
    steps: activeSteps,
    phases: activeFlow.phases,
    selfServiceData,
    startStep
  });

  const currentStep = activeSteps[currentStepId] || activeSteps[startStep];

  const friendlyStepName = useMemo(() => {
    const fallbackName = currentStep?.name ?? null;
    if (resolvedFlowId === 'pas') {
      const configName = FlowConfig.steps[displayStepId]?.name;
      if (configName) {
        return configName;
      }
      return fallbackName ?? FlowConfig.steps[startStep]?.name ?? null;
    }
    return fallbackName ?? FlowConfig.steps[startStep]?.name ?? null;
  }, [resolvedFlowId, displayStepId, currentStep, startStep]);

  const contextValue: WizardContextValue = useMemo(() => ({
    flowId,
    selfServiceData,
    currentStepId,
    history,
    sessionId,
    updateSelfServiceData,
    goToStep,
    goBack,
    goNext
  }), [flowId, selfServiceData, currentStepId, history, sessionId, updateSelfServiceData, goToStep, goBack, goNext]);

  return {
    isLoading,
    currentStepId,
    progress,
    stepName: friendlyStepName ?? undefined,
    contextValue
  };
};




